import { Injectable } from '@angular/core'

export interface LeaveSyncParticipant {
	element: HTMLElement
	scope: string | undefined
	leave(): Promise<void>
}

/**
 * Application-wide (providedIn: 'root') registry backing `LeaveSyncDirective`. Native
 * `animate.leave` does not propagate across a component boundary - a leave animation declared
 * inside a child component's own template never runs when an ancestor removes the whole component
 * in one shot (see docs/animations-migration.md's Pattern A note, and
 * https://github.com/angular/angular/issues/70131). This coordinator lets a component's own
 * `LeaveSyncDirective` instance drive every other registered instance nested inside it once Angular
 * actually invokes its (animate.leave) binding.
 *
 * Hierarchy is derived on demand via DOM containment (`Node.contains()`) rather than via Angular's DI
 * tree, so a single trigger only fans out to elements it actually contains - unrelated leave-sync
 * groups elsewhere in the app are never touched. This also means nesting ("a parent inside another
 * parent") needs no special-casing - containment doesn't care how many levels deep a registered
 * element sits.
 */
@Injectable({ providedIn: 'root' })
export class LeaveSyncCoordinator {
	private readonly participants = new Set<LeaveSyncParticipant>()

	register(participant: LeaveSyncParticipant): () => void {
		this.participants.add(participant)
		return () => this.participants.delete(participant)
	}

	/**
	 * Called by whichever `LeaveSyncDirective` instance's own (animate.leave) Angular actually invoked
	 * for a given removal - deterministically the outermost registered element being removed in that
	 * single shot, since Angular only ever invokes the outermost bound instance's callback for a given
	 * removal. Syncs every OTHER currently registered participant that sits inside it AND shares its
	 * scope, returning each of their completion promises; unrelated participants (elsewhere in the app,
	 * or a different scope) are left untouched.
	 */
	trigger(source: HTMLElement, scope: string | undefined): Promise<void>[] {
		const pending: Promise<void>[] = []
		for (const participant of this.participants) {
			if (participant.element === source) continue
			if (participant.scope !== scope) continue
			if (source.contains(participant.element)) {
				pending.push(participant.leave())
			}
		}
		return pending
	}
}
